<template>
  <div
    v-if="show"
    class="fixed inset-0 bg-black/50 flex items-center justify-center z-9999 p-4"
    @click.self="$emit('close')"
  >
    <div class="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" @click.stop>
      <div class="flex justify-between items-center mb-6">
        <h3 class="text-xl font-bold text-gray-800">
          {{ showSuccess ? 'Email envoyé' : 'Réinitialiser le mot de passe' }}
        </h3>
        <button
          @click="$emit('close')"
          class="text-gray-500 cursor-pointer hover:text-gray-700 text-2xl transition-colors"
        >
          ×
        </button>
      </div>

      <!-- État succès -->
      <div v-if="showSuccess" class="text-center py-4">
        <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
          <svg class="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h4 class="text-lg font-semibold text-gray-800 mb-2">Email envoyé avec succès</h4>
        <p class="text-gray-600 mb-6">
          Un lien de réinitialisation a été envoyé à <strong>{{ email }}</strong>.
          Veuillez vérifier votre boîte mail, y compris vos <strong class="text-orange-500">spams</strong>.
        </p>
        <button
          @click="$emit('close')"
          class="w-full bg-blue-600 cursor-pointer hover:bg-blue-700 text-white px-4 py-3 rounded-lg transition-colors"
        >
          Fermer
        </button>
      </div>

      <!-- Formulaire de réinitialisation -->
      <form v-else @submit.prevent="handleSubmit" class="space-y-4">
        <div>
          <label for="resetEmail" class="block text-sm font-medium text-gray-700 mb-2">
            Adresse email
          </label>
          <input
            id="resetEmail"
            v-model="email"
            type="email"
            class="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-sm transition-all duration-200"
            :class="localErrors.email ? 'border-red-500 focus:ring-red-500' : ''"
            placeholder="Entrez votre adresse email"
            required
          />
          <span v-if="localErrors.email" class="text-red-500 text-sm mt-1 block">
            {{ localErrors.email }}
          </span>
        </div>

        <div class="flex space-x-3 pt-4">
          <button
            type="button"
            @click="$emit('close')"
            class="flex-1 px-4 py-2 cursor-pointer border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Annuler
          </button>
          <button
            type="submit"
            class="flex-1 bg-blue-600 cursor-pointer hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-60"
            :disabled="isLoading"
          >
            {{ isLoading ? 'Envoi...' : 'Envoyer' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, watch } from 'vue'

const props = defineProps({
  show: Boolean,
  isLoading: Boolean,
  initialEmail: String,
  success: Boolean // Nouvelle prop pour indiquer le succès
})

const emit = defineEmits(['close', 'forgot-password-submit'])

const email = ref(props.initialEmail || '')
const showSuccess = ref(false)

const localErrors = reactive({})

// Surveiller le changement de la prop success
watch(
  () => props.success,
  (newSuccess) => {
    if (newSuccess) {
      showSuccess.value = true
    }
  }
)

// Réinitialiser l'état quand le modal est fermé
watch(
  () => props.show,
  (newShow) => {
    if (!newShow) {
      // Réinitialiser après un délai pour permettre l'animation de fermeture
      setTimeout(() => {
        showSuccess.value = false
      }, 300)
    }
  }
)

watch(
  () => props.initialEmail,
  (newEmail) => {
    if (newEmail) {
      email.value = newEmail
    }
  }
)

const validateForm = () => {
  localErrors.email = ''
  if (!email.value.trim()) {
    localErrors.email = "L'adresse email est requise"
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value)) {
    localErrors.email = "L'adresse email n'est pas valide"
  }
  return Object.keys(localErrors).every((key) => !localErrors[key])
}

const handleSubmit = () => {
  if (validateForm()) {
    emit('forgot-password-submit', email.value)
  }
}
</script>

<style scoped></style>
